import React from 'react';
import styled from 'styled-components';
import { Star, AccessTime } from '@mui/icons-material';
import { FoodItem } from '../types';
import { colors, borderRadius, shadows, spacing, typography } from '../styles/theme';

const Card = styled.div`
  background: ${colors.backgroundSecondary};
  border-radius: ${borderRadius.lg};
  box-shadow: ${shadows.sm};
  overflow: hidden;
  margin-bottom: ${spacing.md};
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${shadows.md};
  }

  &:active {
    transform: translateY(0);
  }
`;

const ImageContainer = styled.div`
  position: relative;
  height: 180px;
  overflow: hidden;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const RatingBadge = styled.div`
  position: absolute;
  top: ${spacing.sm};
  right: ${spacing.sm};
  background: rgba(255, 193, 7, 0.95);
  color: ${colors.textPrimary};
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${borderRadius.full};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  box-shadow: ${shadows.sm};
`;

const Content = styled.div`
  padding: ${spacing.md};
`;

const Title = styled.h3`
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.textPrimary};
  margin: 0 0 ${spacing.xs} 0;
`;

const Description = styled.p`
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.sm};
  color: ${colors.textSecondary};
  margin: 0 0 ${spacing.md} 0;
  line-height: ${typography.lineHeight.relaxed};
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Price = styled.span`
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.primary};
`;

const TimeInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  font-size: ${typography.fontSize.sm};
  color: ${colors.textTertiary};
  font-family: ${typography.fontFamily.primary};
  font-weight: ${typography.fontWeight.medium};
`;

const BadgeContainer = styled.div`
  display: flex;
  gap: ${spacing.xs};
  margin-top: ${spacing.sm};
`;

const Badge = styled.span`
  background: ${colors.backgroundTertiary};
  color: ${colors.textSecondary};
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
`;

interface FoodCardProps {
  item: FoodItem;
  onClick: () => void;
}

const FoodCard: React.FC<FoodCardProps> = ({ item, onClick }) => {
  const getBadges = () => {
    const badges = [];
    if (item.isVegetarian) badges.push('Вегетарианское');
    if (item.isVegan) badges.push('Веганское');
    if (item.isGlutenFree) badges.push('Без глютена');
    return badges;
  };

  return (
    <Card onClick={onClick}>
      <ImageContainer>
        <Image src={item.image} alt={item.name} />
        <RatingBadge>
          <Star sx={{ fontSize: 14 }} />
          {item.rating}
        </RatingBadge>
      </ImageContainer>

      <Content>
        <Title>{item.name}</Title>
        <Description>{item.description}</Description>

        <BadgeContainer>
          {getBadges().map((badge) => (
            <Badge key={badge}>{badge}</Badge>
          ))}
        </BadgeContainer>

        <Footer>
          <Price>{item.price}₽</Price>
          <TimeInfo>
            <AccessTime sx={{ fontSize: 16 }} />
            {item.preparationTime} мин
          </TimeInfo>
        </Footer>
      </Content>
    </Card>
  );
};

export default FoodCard;
